import React, { useMemo, useState } from 'react';
import I18n from 'i18n-js';
import { RCButton, RCIcon } from '@reverbdotcom/cadence/components';
import { NudgeExploreIcon } from '@reverbdotcom/cadence/icons/react';
import { Listing } from '@reverbdotcom/commons/src/gql/graphql';

import BumpModalV2WithController from './footer_buttons/BumpButtonV2';
import { bumpRatePercentToDecimal, recommendationAmount } from '../bump/bump_helpers';
import { RCTextUtility } from '@reverbdotcom/cadence/components/RCText/RCText';
import { INSIGHT, trackClicked, useTrackingContext, useTrackImpression } from './sellerRecommendationTracking';

interface Props {
  listing: Listing;
}

export const COMPONENT_NAME = 'LowVisibilityPresentment';

export default function LowVisibilityPresentment({ listing }: Props) {
  const [showBumpModal, setShowBumpModal] = useState(false);
  const bumpRate = listing.bumpRate?.rate || 0;
  const trackingContext = useTrackingContext();

  const recommendedBumpRate = recommendationAmount(listing) ?? undefined;
  const details = { recommendedBumpRate };

  // Memoized on the rate rather than the details object: this reaches the bump modal's
  // success effect deps, and a fresh object each render would re-fire it.
  const bumpSource = useMemo(
    () => ({ insight: INSIGHT.HIGH_COMPETITION_NEW, componentName: COMPONENT_NAME, details: { recommendedBumpRate } }),
    [recommendedBumpRate],
  );

  useTrackImpression(COMPONENT_NAME, INSIGHT.HIGH_COMPETITION_NEW, listing.id, details);

  return (
    <>
      <div className="seller-listing-insight-presentment">
        <span className="seller-listing-insight-presentment__low-visibility">
          <RCIcon icon={<NudgeExploreIcon />} size="inline-prominent" spacing="right" color="accent-loud" />
          <span className="weight-bold">
            {I18n.t('discovery.listingsManagement.sellerListingCard.insights.lowVisibility.title')}
          </span>
        </span>
        <RCTextUtility size="350">
          {I18n.t('discovery.listingsManagement.sellerListingCard.insights.lowVisibility.bumpToBoost')}
        </RCTextUtility>
        <RCButton
          variant="outlined"
          size="small"
          onClick={() => {
            trackClicked(COMPONENT_NAME, INSIGHT.HIGH_COMPETITION_NEW, listing.id, trackingContext, details);
            setShowBumpModal(true);
          }}
          fullWidth
        >
          {I18n.t('discovery.listingsManagement.sellerListingCard.promoteWithBump')}
        </RCButton>
      </div>

      <BumpModalV2WithController
        listingId={listing.id}
        bid={bumpRatePercentToDecimal(bumpRate)}
        showModal={showBumpModal}
        setShowModal={setShowBumpModal}
        listing={listing}
        source={bumpSource}
      />
    </>
  );
}
